import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';

/**
 * <PhotoNavArrow>
 *
 * The one prev/next arrow for every multi-photo surface — feed cards, hero
 * galleries, memory cards, the lightbox.
 *
 * ## Why it exists
 *
 * Eight surfaces grew their own arrow independently and every one of them
 * reached the same wrong conclusion: "touch has no hover, so the arrow must
 * stay visible or the photos are unreachable." True at the time — only
 * UnifiedCard/PhotoCarousel had a swipe gesture — but it meant a permanent
 * 40–48px control parked over the middle of every photo on a phone, which is
 * the loudest chrome on the surface whose entire job is showing a picture. The
 * treatments had also drifted (opacity 0.7/0.8/1.0, five ground colours, and
 * MemoryLaneCard rendered its arrows LARGER on mobile than on desktop).
 *
 * With src/hooks/useSwipe.js on every surface, the premise is gone: touch users
 * swipe, so the arrow is free to be what it always should have been — a pointer
 * affordance that appears on hover.
 *
 * ## Behaviour
 *
 * `hidden can-hover:flex` — the arrow does not render at all where the pointer
 * can't hover. Not `opacity-0`: an invisible-but-present button still swallows
 * the taps aimed at the photo behind it (this was a live bug in
 * PlannerTripCard) and still offers screen-reader users on touch a control that
 * pointer-events suppression would stop them activating. Absent is honest.
 *
 * On hover-capable devices it fades in with the parent's hover and is revealed
 * by `:focus-visible`, so keyboard users on those devices never lose it. Note
 * the scope: `display: none` takes the arrow out of the tab order entirely, so
 * on a device that reports no hover — an iPad with a keyboard attached still
 * reports `pointer: coarse` — there is no keyboard route to it either. That is
 * the whole reason requirement 2 below is a requirement and not a suggestion.
 *
 * `disabled` renders the arrow dimmed rather than hidden, and it needs the `!`
 * to do it: `.group:hover .group-hover\:opacity-100` is specificity (0,3,0)
 * against a plain `disabled:opacity-*` at (0,2,0), so without the override a
 * disabled arrow fades in at FULL opacity on hover and reads as a live control
 * that does nothing when clicked.
 *
 * ## Requirements at the call site
 *
 * 1. The positioned ancestor must carry Tailwind's `group` class, or the
 *    hover reveal never fires.
 * 2. Touch users must have another route to the same photos — the swipe hook,
 *    plus a dot row for anything a screen reader should be able to page. A
 *    surface that hides these arrows without a gesture is unreachable on a
 *    phone, which is the state this component exists to end.
 *
 * @param {'left'|'right'} direction
 * @param {function} onClick
 * @param {boolean} [disabled] - dims to 40% and blocks; for bounded (non-wrapping) sets
 * @param {'overPhoto'|'overBackdrop'} [variant] - ground it sits on. overPhoto
 *   is a scrim pill for arrows on top of the image itself; overBackdrop is the
 *   lighter translucent pill for the lightbox's own black field.
 * @param {'sm'|'md'|'lg'} [size]
 * @param {string} [label] - aria-label override; defaults to "Previous/Next photo"
 * @param {string} [className] - extra positioning (defaults inset from the edge)
 */

const VARIANTS = {
  overPhoto: 'bg-black/45 backdrop-blur-sm text-content-inverse hover:bg-black/65',
  overBackdrop: 'bg-content-inverse/10 hover:bg-content-inverse/20 text-content-inverse'
};

const SIZES = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12'
};

const ICON_SIZES = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6'
};

function PhotoNavArrow({
  direction,
  onClick,
  disabled = false,
  variant = 'overPhoto',
  size = 'md',
  label,
  className = ''
}) {
  const { t } = useTranslation();
  const isLeft = direction === 'left';

  return (
    <button
      type="button"
      onClick={(e) => {
        // Photo surfaces are routinely nested inside a clickable card or a
        // backdrop that closes on click; paging must not also navigate away.
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      disabled={disabled}
      aria-label={label || (isLeft ? t('photoNav.previousAriaLabel') : t('photoNav.nextAriaLabel'))}
      className={`hidden can-hover:flex absolute ${isLeft ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 z-raised ${SIZES[size]} items-center justify-center rounded-full ${VARIANTS[variant]} opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-all duration-normal focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2 disabled:!opacity-40 disabled:cursor-not-allowed ${className}`}
    >
      <Icon name={isLeft ? 'chevronLeft' : 'chevronRight'} className={ICON_SIZES[size]} />
    </button>
  );
}

export default PhotoNavArrow;
